import { useState, useEffect } from 'react';
import LoginPage from './pages/LoginPage';
import PlanningGrid from './components/Planning/PlanningGrid';
import UserManager from './components/Admin/UserManager';
import PatientManager from './components/Admin/PatientManager';
import RecurrenceManager from './components/Admin/RecurrenceManager';
import SettingsPage from './pages/SettingsPage';
import Sidebar from './components/UI/Sidebar';
import MobilePlanning from './components/Planning/MobilePlanning';
import useIsMobile from './hooks/useIsMobile';
import usePreferences from './hooks/usePreferences';
import SecteurManager from './components/Admin/SecteurManager';
import LogManager from './components/Admin/LogManager';
import StatsPage from './pages/StatsPage'; // à créer
import PatientFiche from './components/Admin/PatientFiche';
import useAchievements from './hooks/useAchievements';
import AchievementToast from './components/UI/AchievementToast';
import AchievementsPage from './pages/AchievementsPage';
import AchievementsWikiPage from './pages/AchievementsWikiPage';
import PatientsMap from './components/Planning/PatientsMap';
import '@tabler/icons-webfont/dist/tabler-icons.min.css';
import AclPage from './pages/AclPage';
import useAcl from './hooks/useAcl';
import AgenceManager from './components/Admin/AgenceManager';
import useVersionCheck from './hooks/useVersionCheck';
import BugReportButton from './components/UI/BugReportButton';
import BugReportsPage from './pages/BugReportsPage';

function isTokenValid(token) {
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 > Date.now();
    } catch (e) {
        return false;
    }
}

function App() {
    const isMobile = useIsMobile();
    const [isLoggedIn, setIsLoggedIn] = useState(() => isTokenValid(localStorage.getItem('token')));
    const [activePage, setActivePage] = useState('planning');
    const { preferences, savePreferences } = usePreferences();
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const { newlyUnlocked, checkAndUnlock, checkThresholds, dismissNotification } = useAchievements();
    const [mapOpenCount, setMapOpenCount] = useState(0);
    const { can, aclLoading } = useAcl();
    const [pagesVisitees, setPagesVisitees] = useState(new Set());
    const updateAvailable = useVersionCheck();
    const [statsOpenCount, setStatsOpenCount] = useState(0);
    const [weekNavCount, setWeekNavCount] = useState(0);

    useEffect(() => {
        document.documentElement.style.setProperty('--accent-color', preferences.accentColor || '#4f46e5');
    }, [preferences.accentColor]);

    useEffect(() => {
        const dark = preferences.darkMode;
        const root = document.documentElement.style;

        root.setProperty('--card-bg', dark ? '#1e293b' : 'white');
        root.setProperty('--card-bg-secondary', dark ? '#0f172a' : '#f8fafc');
        root.setProperty('--text-main', dark ? '#f0f2f5' : '#1a1a2e');
        root.setProperty('--text-muted', dark ? '#94a3b8' : '#888');
        root.setProperty('--border-light', dark ? '#334155' : '#f0f0f0');
        root.setProperty('--border-default', dark ? '#334155' : '#e0e0e0');
        root.setProperty('--input-bg', dark ? '#1e293b' : 'white');
        root.setProperty('--hover-bg', dark ? '#334155' : '#f0f2f5');

        if (dark) {
            checkAndUnlock('createure_nuit');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferences.darkMode]);

    useEffect(() => {
        document.body.style.backgroundColor = preferences.darkMode ? '#1a1a2e' : '#f0f2f5';
        document.body.style.color = preferences.darkMode ? '#f0f2f5' : '#1a1a2e';
    }, [preferences.darkMode]);

    useEffect(() => {
        if (!isLoggedIn) return;

        const today = new Date().toISOString().substring(0, 10);
        const lastLogin = localStorage.getItem('last_login_date');
        const streak = parseInt(localStorage.getItem('login_streak') || '0');

        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().substring(0, 10);

        if (lastLogin === yesterdayStr) {
            const newStreak = streak + 1;
            localStorage.setItem('login_streak', newStreak);
            if (newStreak >= 5) checkAndUnlock('fidele');
        } else if (lastLogin !== today) {
            localStorage.setItem('login_streak', '1');
        }
        localStorage.setItem('last_login_date', today);

        const checkToken = () => {
            if (!isTokenValid(localStorage.getItem('token'))) {
                localStorage.removeItem('token');
                setIsLoggedIn(false);
            }
        };

        const interval = setInterval(checkToken, 30000);
        window.addEventListener('focus', checkToken);

        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', checkToken);
        };
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) return;
        const now = new Date();
        const mois = now.getMonth(); // 0-indexé
        const heure = now.getHours();
        const jour = now.getDay();

        if (jour === 0 || jour === 6) checkAndUnlock('weekend_warrior');
        if (heure >= 0 && heure < 5) checkAndUnlock('noctambule');
        if (heure < 6) checkAndUnlock('matinal');
        if (jour === 1 && heure < 7) checkAndUnlock('lundi_blues');
        if (heure === 9 && new Date().getMinutes() < 20) checkAndUnlock('cafe_virtuel');
        if (mois === 11 && (jour === 24 || jour === 25)) checkAndUnlock('nuit_noel');
        if (mois === 3 && jour === 1) checkAndUnlock('poisson_avril');
        if (mois === 0 && jour === 1) checkAndUnlock('bonne_annee');

        if (jour === 5) checkAndUnlock('vendredi'); // 5 = vendredi
// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) return;
        const timer = setTimeout(() => {
            checkAndUnlock('marathon');
        }, 2 * 60 * 60 * 1000); // 2h en ms
        return () => clearTimeout(timer);
// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) return;
        if (localStorage.getItem('haut_fait_petit_curieux')) {
            localStorage.removeItem('haut_fait_petit_curieux');
            checkAndUnlock('petit_curieux');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
    };

    const handleNavigate = (page) => {
        if (page !== 'patients') setSelectedPatientId(null);
        setActivePage(page);
        if (page === 'map') {
            const newCount = mapOpenCount + 1;
            setMapOpenCount(newCount);
            if (newCount >= 10) checkAndUnlock('cartographe');
        }
        const newPages = new Set(pagesVisitees).add(page);
        setPagesVisitees(newPages);
        const toutesLesPages = ['planning', 'patients', 'recurrences', 'secteurs', 'map', 'settings', 'logs', 'stats', 'achievements'];
        if (toutesLesPages.every(p => newPages.has(p))) {
            checkAndUnlock('explorateur');
        }
        if (page === 'stats') {
            const newCount = statsOpenCount + 1;
            setStatsOpenCount(newCount);
            if (newCount >= 10) checkAndUnlock('statisticien');
        }
    };

    if (!isLoggedIn) {
        return <LoginPage onLogin={() => setIsLoggedIn(true)} />;
    }

    // Vue mobile
    if (isMobile) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
                <header style={{
                    backgroundColor: '#1a1a2e',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'white' }}>
                        📋 Planning
                    </h1>
                    <button
                        onClick={handleLogout}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer' }}
                    >
                        🚪 Déconnexion
                    </button>
                </header>
                <MobilePlanning />
                <AchievementToast achievement={newlyUnlocked} onDismiss={dismissNotification} />
            </div>
        );
    }

    const renderPage = () => {
        if (aclLoading) return <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>Chargement...</div>;

        switch (activePage) {
            case 'planning':
                return <PlanningGrid key={JSON.stringify(preferences.livreurColors)} preferences={preferences} checkAndUnlock={checkAndUnlock} checkThresholds={checkThresholds} />
            case 'users':
                return (
                    <div style={{ padding: '24px' }}>
                        <h2 style={{ margin: '0 0 24px', fontSize: '22px', fontWeight: '700' }}>Utilisateurs</h2>
                        <UserManager />
                    </div>
                );
            case 'patients':
                if (!can('voir_patients')) return <div style={{padding:'24px'}}>Accès non autorisé</div>;
                return selectedPatientId ? (
                    <PatientFiche patientId={selectedPatientId} onBack={() => setSelectedPatientId(null)} />
                ) : (
                    <div style={{ padding: '24px' }}>
                        <h2 style={{ margin: '0 0 24px', fontSize: '22px', fontWeight: '700' }}>Patients</h2>
                        <PatientManager onSelectPatient={setSelectedPatientId} />
                    </div>
                );
            case 'recurrences':
                if (!can('voir_recurrences')) return <div style={{padding:'24px'}}>Accès non autorisé</div>;
                return (
                    <div style={{ padding: '24px' }}>
                        <h2 style={{ margin: '0 0 24px', fontSize: '22px', fontWeight: '700' }}>Récurrences</h2>
                        <RecurrenceManager />
                    </div>
                );
            case 'secteurs':
                return (
                    <div style={{ padding: '24px' }}>
                        <h2 style={{ margin: '0 0 24px', fontSize: '22px', fontWeight: '700' }}>Secteurs</h2>
                        <SecteurManager />
                    </div>
                );
            case 'agences':
                return (
                    <div style={{ padding: '24px' }}>
                        <h2 style={{ margin: '0 0 24px', fontSize: '22px', fontWeight: '700' }}>Agences</h2>
                        <AgenceManager />
                    </div>
                );
            case 'settings':
                return <SettingsPage preferences={preferences} savePreferences={savePreferences} />;
            case 'logs':
                if (!can('voir_logs')) return <div style={{padding:'24px'}}>Accès non autorisé</div>;
                return (
                    <div style={{ padding: '24px' }}>
                        <h2 style={{ margin: '0 0 24px', fontSize: '22px', fontWeight: '700' }}>Logs d'activité</h2>
                        <LogManager />
                    </div>
                );
            case 'stats':
                if (!can('voir_stats')) return <div style={{padding:'24px'}}>Accès non autorisé</div>;
                return <StatsPage />;
            case 'acl':
                return <AclPage />;
            case 'achievements':
                return <AchievementsPage checkAndUnlock={checkAndUnlock} />;
            case 'achievements-wiki':
                return <AchievementsWikiPage />;
            case 'map':
                if (!can('voir_carte')) return <div style={{padding:'24px'}}>Accès non autorisé</div>;
                return <PatientsMap />;
            case 'bug-reports':
                return <BugReportsPage />;
            default:
                return <PlanningGrid preferences={preferences} checkAndUnlock={checkAndUnlock} checkThresholds={checkThresholds} />
        }
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh' }}>
            <Sidebar
                activePage={activePage}
                onNavigate={handleNavigate}
                onLogout={handleLogout}
                can={can}
                checkAndUnlock={checkAndUnlock}
            />
            <div style={{ flex: 1, overflow: 'auto', backgroundColor: preferences.darkMode ? '#0f172a' : '#f0f2f5' }}>
                {renderPage()}
            </div>
            {updateAvailable && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
                    backgroundColor: '#4f46e5', color: 'white',
                    padding: '10px 20px', textAlign: 'center', fontSize: '14px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px'
                }}>
                    <i className="ti ti-refresh-alert" />
                    Une nouvelle version est disponible.
                    <button onClick={() => window.location.reload()} style={{
                        background: 'white', color: '#4f46e5',
                        border: 'none', borderRadius: '6px',
                        padding: '4px 12px', fontWeight: '600', cursor: 'pointer'
                    }}>
                        Recharger
                    </button>
                </div>
            )}
            <BugReportButton checkAndUnlock={checkAndUnlock} />
            <AchievementToast achievement={newlyUnlocked} onDismiss={dismissNotification} />
        </div>
    );
}

export default App;